import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Checkbox,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import BarraElenco from '../common/BarraElenco';
import { TESTATA_TABELLA } from '../common/stiliTabella';

// Le voci del selettore. I codici sono quelli che il backend accetta su `scope`.
const AMBITI = [
    { value: 'organization', label: 'Organizzazione' },
    { value: 'group', label: 'Gruppo' },
    { value: 'own', label: 'Propri' },
];

const AMBITO_PREDEFINITO = 'organization';

// L'altezza della casella piccola di MUI: 24px d'icona piu' 9px di padding per lato. Ogni
// cella riserva questa banda alla casella, anche quando sotto non c'e' nulla, cosi' le caselle
// di una riga stanno tutte sulla stessa linea. Senza, la cella col selettore e' piu' alta,
// l'allineamento verticale la centra, e la sua casella sale rispetto alle altre: si vede una
// scalinata, e non si capisce piu' quale casella appartenga a quale colonna.
const ALTEZZA_CASELLA = 42;

// Fuori dalla vista ma dentro l'albero di accessibilità. Non `display: none` né
// `visibility: hidden`: entrambi tolgono l'elemento anche ai lettori di schermo, che è
// precisamente ciò che qui non si vuole.
const SOLO_LETTORI_DI_SCHERMO = {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
};

// Il prezzo di «Gruppo»: che cosa quell'ambito filtra davvero. Vive in un tooltip accanto al
// selettore, non in una riga di prosa dentro la cella — quattro righe di testo per cella
// facevano crescere la riga della tabella fino a rendere illeggibile la griglia, che e' la
// cosa che questa schermata serve a leggere.
//
// L'icona resta condizionata all'ambito scelto: un'icona che c'e' sempre, anche sotto
// «Organizzazione», rimanda a un testo che parla di un'opzione non scelta.
//
// La chiave arriva dal catalogo (`groupScopeKind`), perché «Gruppo» non significa la stessa
// cosa sulle cinque risorse filtrate — su un Codex somma il tipo e le code, su
// un'elaborazione le sole code, su un tipo di Codex il solo legame al gruppo: dedurla qui da
// un codice di risorsa vorrebbe dire tenere in JavaScript una copia della tassonomia.
//
// Dalla fase F5 le celle con il selettore sono undici e non più quattro, e le tre azioni di
// amministrazione sulle code (`queues.create/update/delete`) riusano la chiave della lettura
// — `QUEUE_BY_GROUP` — perché la definizione di «le code dei miei gruppi» è una sola nel
// backend. Qui non va quindi aggiunta una voce per ciascuna azione: sarebbe la seconda copia
// di una prosa che il backend tiene già allineata a una sola semantica.
const PREZZO_DEL_GRUPPO = {
    CODEX_BY_GROUP:
        "«Gruppo»: solo i Codex il cui tipo sta in un gruppo in cui questo permesso è " +
        "concesso alla persona, oppure il cui processo tocca una coda di quegli stessi " +
        "gruppi. I due criteri si sommano. Sulle code conta come il processo è " +
        "configurato, non il lavoro svolto. Un tipo che non sta in nessun gruppo resta " +
        "visibile solo a chi ha ambito Organizzazione.",
    PROCESS_RUN_BY_QUEUES:
        "«Gruppo»: solo le elaborazioni il cui processo tocca una coda dei gruppi in cui " +
        "questo permesso è concesso alla persona — non lo stesso insieme dei Codex. Senza " +
        "code nei gruppi, nessuna elaborazione.",
    QUEUE_BY_GROUP:
        "«Gruppo»: solo le code dei gruppi in cui questo permesso è concesso alla persona. " +
        "Un gruppo senza code non fa vedere nessuna coda, e un altro ruolo in un altro " +
        "gruppo non ne aggiunge: l'ambito è per concessione, non per unione.",
    CODEX_TYPE_BY_GROUP:
        "«Gruppo»: solo i tipi di Codex contenuti nei gruppi in cui questo permesso è " +
        "concesso alla persona. Qui non c'è la seconda porta del Codex — nessuna coda entra " +
        "nel conto. Un tipo che non sta in nessun gruppo resta visibile solo a chi ha ambito " +
        "Organizzazione, come i suoi documenti: per legarlo a un gruppo si passa dalla " +
        "pagina Gruppi, che li elenca tutti. Con questo ambito un tipo creato nasce già " +
        "legato ai gruppi di chi lo crea.",
    TASK_FOLLOWS_QUEUE:
        "«Gruppo» non filtra le attività: si vedono quelle delle code già visibili.",
};

// Il prezzo di «Propri», che e' il piu' alto dei tre. Stessa forma e stesso posto del prezzo
// di «Gruppo»: un tooltip, reso solo quando «Propri» e' l'ambito scelto.
//
// La chiave e' la stessa di PREZZO_DEL_GRUPPO perche' identifica la risorsa, e «Propri»
// oggi ne ha una sola: il catalogo lo offre dove `ownScopeAllowed`, e il backend lo concede
// alle sole Attivita' in coda (l'unica con un assegnatario). Se un domani una seconda
// risorsa guadagnasse un proprietario, una chiave sconosciuta qui non mostra nulla invece di
// mostrare la prosa sbagliata: il verso giusto in cui sbagliare.
const PREZZO_DEI_PROPRI = {
    TASK_FOLLOWS_QUEUE:
        "«Propri»: solo le attività già assegnate. Non si prende lavoro nuovo dalla coda, " +
        "nemmeno un'attività libera, e il rilascio è definitivo — l'attività torna libera e " +
        "sparisce. Per chi deve prendersi il lavoro da sé, l'ambito è «Gruppo».",
};

// I sei permessi con ambito proprio che nessun filtro consulta: l'ambito effettivo è quello
// della lettura della stessa risorsa, e dirlo evita di far credere a una restrizione assente.
const AMBITO_SEGUE_LA_LETTURA =
    "Ambito: quello della Consulta della stessa risorsa. Ciò che non si vede non si può " +
    "modificare.";

// Riferita in calce alla griglia, e senza il numero del paragrafo di specifica: «(§5.1)» a
// schermo rimanda a un documento che chi configura un ruolo non ha aperto, e assomiglia a una
// nota rimasta dentro per sbaglio.
const REGOLA_COMPOSIZIONE =
    "Chi ha più ruoli somma i permessi di tutti. Dove la stessa azione arriva da due ruoli " +
    "con ambiti diversi, vale l'ambito più ampio dei due.";

/**
 * Il testo in sola lettura di una cella spuntata che non ha un selettore.
 *
 * Le celle con `scopeEnforced` non passano di qui: hanno il selettore.
 */
function testoAmbito(voce) {
    if (voce.scopeCapability === 'OWN_SCOPE') {
        return AMBITO_SEGUE_LA_LETTURA;
    }
    if (voce.scopeCapability === 'INHERITED_FROM_CODEX') {
        // L'etichetta della chiave dell'ereditarietà arriva dal catalogo, dove la genera
        // `Permission.label()`: ricopiarla qui lascerebbe a schermo, dopo un rinomino della
        // risorsa, un nome che a schermo non esiste più.
        if (!voce.inheritedFromLabel) {
            return 'Ambito ereditato dal Codex.';
        }
        return `Ambito ereditato dal Codex, tramite «${voce.inheritedFromLabel}»: senza quel `
            + 'permesso questa risorsa non è visibile.';
    }
    return null;
}

/**
 * La nota appesa alla casella di una cella: la nota del catalogo, l'ambito in sola lettura, o
 * le due unite.
 *
 * Sono due cose diverse che finiscono nello stesso tooltip. La nota del catalogo (`voce.nota`)
 * dice che cosa il permesso concede davvero, e vale **anche a casella non spuntata** — è lì che
 * serve, prima di concedere. Il testo d'ambito descrive un permesso già concesso. Unirle invece
 * di sceglierne una evita che concedere un permesso faccia sparire la nota che ne spiegava il
 * significato.
 *
 * Nessuna cella oggi ha entrambe — `users.update` non è filtrabile — ma il tooltip non è il
 * posto dove far dipendere la correttezza da quel fatto.
 */
function notaDellaCasella(voce, ambito) {
    return [voce.nota, ambito].filter(Boolean).join(' ') || null;
}

/**
 * Cosa comporta l'ambito scelto su questa cella, o `null` se non c'è un prezzo da dichiarare.
 *
 * «Organizzazione» non ha prezzo: non restringe nulla. Gli altri due lo hanno, e si dichiara
 * **solo quello scelto**: un avviso che compare comunque parla di un'opzione che l'utente non
 * ha scelto, si ripete in ogni cella filtrata e smette di essere letto.
 */
function prezzoDi(conSelettore, ambitoScelto, voce) {
    if (!conSelettore) {
        return null;
    }
    if (ambitoScelto === 'group') {
        return PREZZO_DEL_GRUPPO[voce.groupScopeKind] || null;
    }
    if (ambitoScelto === 'own') {
        return PREZZO_DEI_PROPRI[voce.groupScopeKind] || null;
    }
    return null;
}

/** Le voci offerte per una cella: «Propri» solo dove esiste un assegnatario da confrontare. */
function ambitiPer(voce) {
    return AMBITI.filter((ambito) => ambito.value !== 'own' || voce.ownScopeAllowed);
}

/**
 * Che cosa taglia via l'ambito scelto, appeso al controllo che lo sceglie.
 *
 * Niente icona: un simbolo in ogni cella filtrata è rumore in una griglia che si legge a colpo
 * d'occhio. Il tooltip sta sul controllo stesso, che è dove la mano è già.
 *
 * Il testo esiste anche fuori dal tooltip, nascosto alla vista e riferito dal controllo con
 * `aria-describedby`: un contenuto che vive solo nel passaggio del mouse è irraggiungibile da
 * tastiera e dai lettori di schermo, e questo è l'unico posto in cui si dichiara che cosa un
 * ambito taglia via.
 */
function ConNotaAmbito({ testo, id, sospeso, children }) {
    // L'apertura la teniamo noi, e `useState` sta prima di qualunque uscita anticipata: un
    // hook dentro un ramo condizionale cambierebbe l'ordine degli hook fra un render e
    // l'altro.
    const [sottoIlPuntatore, setSottoIlPuntatore] = useState(false);

    if (!testo) {
        return children;
    }
    return (
        <>
            {/* Il tooltip e' controllato *sempre*, e non solo quando c'e' da ritrarlo: e' la
                differenza fra questo meccanismo e quello di prima, che non ha mai funzionato.
                `Tooltip` passa `open` a `useControlled`, che congela «controllato o no» al
                primo render:

                    const { current: isControlled } = useRef(controlled !== undefined);
                    const value = isControlled ? controlled : valueState;

                Nascendo con `open={sospeso ? false : undefined}` e `sospeso` inizialmente
                falso, il tooltip restava NON controllato per tutta la vita, e ogni
                `open={false}` successivo veniva ignorato: la nota restava appesa sopra le voci
                fra cui si sta scegliendo, cioe' copriva proprio la scelta che descrive. MUI lo
                denunciava sulla console («changing the uncontrolled open state of Tooltip to
                be controlled»), ed e' quell'errore che il test usa come rete -- il sintomo
                visivo non e' riproducibile in jsdom, dove il menu modale sposta il fuoco e
                chiude il tooltip da se'.

                `onOpen`/`onClose` lasciano a MUI le sue regole di apertura (passaggio del
                mouse, fuoco da tastiera, ritardi): noi ne registriamo l'esito e ci
                sovrapponiamo solo per sospendere. Cadono anche i due `disable*Listener`, che
                servivano a impedire un'apertura che ora non dipende piu' da loro. */}
            <Tooltip
                title={testo}
                arrow
                open={sottoIlPuntatore && !sospeso}
                onOpen={() => setSottoIlPuntatore(true)}
                onClose={() => setSottoIlPuntatore(false)}
            >
                {children}
            </Tooltip>
            <Box id={id} sx={SOLO_LETTORI_DI_SCHERMO}>{testo}</Box>
        </>
    );
}

ConNotaAmbito.propTypes = {
    testo: PropTypes.string,
    id: PropTypes.string.isRequired,
    sospeso: PropTypes.bool,
    children: PropTypes.node.isRequired,
};

ConNotaAmbito.defaultProps = { testo: null, sospeso: false };

/**
 * Il selettore d'ambito di una cella, con la sua nota.
 *
 * È un componente a sé per un motivo solo: deve ricordare se il proprio menu è aperto, per
 * ritrarre il tooltip mentre si sceglie. Uno stato tenuto nella griglia dovrebbe essere una
 * mappa per codice di permesso, e ogni apertura di menu ridisegnerebbe tutte le celle.
 */
function SelettoreAmbito({ voce, ambitoScelto, prezzo, readOnly, onScegli }) {
    const [menuAperto, setMenuAperto] = useState(false);
    const idNota = `nota-${voce.code}`;

    return (
        <ConNotaAmbito testo={prezzo} id={idNota} sospeso={menuAperto}>
            {/* Select non nativo, come ogni altra tendina dell'applicazione: il menu nativo
                del sistema operativo era l'unico controllo con un aspetto proprio in tutta
                l'interfaccia. */}
            <TextField
                select
                size="small"
                disabled={readOnly}
                SelectProps={{
                    onOpen: () => setMenuAperto(true),
                    onClose: () => setMenuAperto(false),
                    inputProps: {
                        'aria-label': `Ambito di ${voce.label}`,
                        'aria-describedby': prezzo ? idNota : undefined,
                    },
                }}
                value={ambitoScelto}
                onChange={(e) => onScegli(e.target.value)}
                sx={{ minWidth: 150 }}
            >
                {ambitiPer(voce).map((opzione) => (
                    <MenuItem key={opzione.value} value={opzione.value}>
                        {opzione.label}
                    </MenuItem>
                ))}
            </TextField>
        </ConNotaAmbito>
    );
}

SelettoreAmbito.propTypes = {
    voce: PropTypes.shape({
        code: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        ownScopeAllowed: PropTypes.bool,
    }).isRequired,
    ambitoScelto: PropTypes.string.isRequired,
    prezzo: PropTypes.string,
    readOnly: PropTypes.bool,
    onScegli: PropTypes.func.isRequired,
};

SelettoreAmbito.defaultProps = { prezzo: null, readOnly: false };

/**
 * Griglia risorse × azioni del dettaglio ruolo (spec §10.3, §6.3).
 *
 * Non chiama il backend: riceve `catalog` e `granted`, restituisce con `onChange` l'insieme
 * COMPLETO dei permessi spuntati (mai un delta, altrimenti il chiamante dovrebbe ricostruire
 * lo stato — modo tipico in cui si perde una spunta).
 *
 * Colonne e righe si derivano dal `catalog`, mai da un elenco scritto qui: le azioni distinte
 * nell'ordine in cui il catalogo le presenta, le risorse distinte raggruppate per `groupCode`.
 * Una cella esiste se e solo se il catalogo ha una voce per quella coppia risorsa/azione;
 * altrimenti è il carattere «—», non una checkbox disabilitata — una casella disabilitata
 * direbbe «esiste ma non te lo do», che è falso (§6.3).
 *
 * Il selettore d'ambito §10.3 compare solo dove il catalogo dichiara `scopeEnforced`, cioè
 * dove un filtro row-level legge davvero `role_permission.scope`. Altrove il controllo
 * salverebbe una restrizione che nessun codice applica — lo stesso difetto per cui il
 * blocco 7 aveva omesso il selettore del tutto. Quali celle siano, e se ammettano «Propri»,
 * lo dice il catalogo: qui non c'è un elenco di codici, che divergerebbe in silenzio da
 * quello vero del backend.
 */
export default function PermissionGrid({ catalog, granted, readOnly, onChange, otherRoles }) {
    const [filtro, setFiltro] = useState('');
    const [copyAnchor, setCopyAnchor] = useState(null);
    const [copiedFrom, setCopiedFrom] = useState(null);

    const grantedCodes = useMemo(() => new Set(granted.map((g) => g.permissionCode)), [granted]);

    const ambitoConcesso = useMemo(() => {
        const mappa = new Map();
        granted.forEach((g) => mappa.set(g.permissionCode, g.scope));
        return mappa;
    }, [granted]);

    const azioni = useMemo(() => {
        const viste = new Set();
        const elenco = [];
        catalog.forEach((voce) => {
            if (!viste.has(voce.actionCode)) {
                viste.add(voce.actionCode);
                elenco.push({ code: voce.actionCode, label: voce.actionLabel });
            }
        });
        return elenco;
    }, [catalog]);

    const gruppi = useMemo(() => {
        const perGruppo = new Map();
        catalog.forEach((voce) => {
            if (!perGruppo.has(voce.groupCode)) {
                perGruppo.set(voce.groupCode, { code: voce.groupCode, label: voce.groupLabel, risorse: new Map() });
            }
            const gruppo = perGruppo.get(voce.groupCode);
            if (!gruppo.risorse.has(voce.resourceCode)) {
                gruppo.risorse.set(voce.resourceCode, { code: voce.resourceCode, label: voce.resourceLabel });
            }
        });
        return Array.from(perGruppo.values());
    }, [catalog]);

    const cellePerRisorsa = useMemo(() => {
        const mappa = new Map();
        catalog.forEach((voce) => {
            if (!mappa.has(voce.resourceCode)) {
                mappa.set(voce.resourceCode, new Map());
            }
            mappa.get(voce.resourceCode).set(voce.actionCode, voce);
        });
        return mappa;
    }, [catalog]);

    const filtroNormalizzato = filtro.trim().toLowerCase();

    function isRisorsaVisibile(risorsa) {
        if (!filtroNormalizzato) {
            return true;
        }
        return risorsa.label.toLowerCase().includes(filtroNormalizzato);
    }

    function emettiToggle(permissionCode, checked, scopeIniziale) {
        if (readOnly) {
            return;
        }
        let nuovoInsieme;
        if (checked) {
            nuovoInsieme = [...granted, { permissionCode, scope: scopeIniziale }];
        } else {
            nuovoInsieme = granted.filter((g) => g.permissionCode !== permissionCode);
        }
        onChange(nuovoInsieme);
    }

    function emettiAmbito(permissionCode, scope) {
        if (readOnly) {
            return;
        }
        onChange(granted.map((g) => (g.permissionCode === permissionCode ? { ...g, scope } : g)));
    }

    function apriMenuCopia(event) {
        setCopyAnchor(event.currentTarget);
    }

    function chiudiMenuCopia() {
        setCopyAnchor(null);
    }

    function copiaDa(ruolo) {
        setCopiedFrom(ruolo.label);
        chiudiMenuCopia();
        onChange(ruolo.granted);
    }

    return (
        <Box>
            <BarraElenco
                ricerca={filtro}
                onRicerca={setFiltro}
                placeholderRicerca="Filtra risorse..."
                conteggio={`${granted.length} ${granted.length === 1 ? 'permesso attivo' : 'permessi attivi'} su ${catalog.length}`}
                azione={
                    otherRoles && otherRoles.length > 0 ? (
                        <Button startIcon={<CopyIcon />} onClick={apriMenuCopia} disabled={readOnly}>
                            Copia da un altro ruolo
                        </Button>
                    ) : null
                }
            />
            <Menu anchorEl={copyAnchor} open={Boolean(copyAnchor)} onClose={chiudiMenuCopia}>
                {(otherRoles || []).map((ruolo) => (
                    <MenuItem key={ruolo.id} onClick={() => copiaDa(ruolo)}>
                        {ruolo.label}
                    </MenuItem>
                ))}
            </Menu>
            {copiedFrom && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Permessi copiati da {copiedFrom}
                </Typography>
            )}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={TESTATA_TABELLA}>
                            <TableCell>Risorsa</TableCell>
                            {azioni.map((azione) => (
                                <TableCell key={azione.code} role="columnheader" align="center">
                                    {azione.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {gruppi.map((gruppo) => {
                            const risorseVisibili = Array.from(gruppo.risorse.values()).filter(isRisorsaVisibile);
                            if (risorseVisibili.length === 0) {
                                return null;
                            }
                            return (
                                <React.Fragment key={gruppo.code}>
                                    <TableRow>
                                        <TableCell colSpan={azioni.length + 1} sx={{ bgcolor: 'action.hover' }}>
                                            <Typography variant="overline">{gruppo.label.toUpperCase()}</Typography>
                                        </TableCell>
                                    </TableRow>
                                    {risorseVisibili.map((risorsa) => (
                                        <TableRow key={risorsa.code}>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <Box sx={{ height: ALTEZZA_CASELLA, display: 'flex', alignItems: 'center' }}>
                                                    {risorsa.label}
                                                </Box>
                                            </TableCell>
                                            {azioni.map((azione) => {
                                                const voce = cellePerRisorsa.get(risorsa.code)?.get(azione.code);
                                                if (!voce) {
                                                    return (
                                                        <TableCell
                                                            key={azione.code}
                                                            role="cell"
                                                            align="center"
                                                            sx={{ verticalAlign: 'top' }}
                                                        >
                                                            {/* Il trattino sta nella stessa banda della casella:
                                                                allineato con le caselle delle altre colonne, non
                                                                col selettore che qualcuna di esse ha sotto. */}
                                                            <Box
                                                                sx={{
                                                                    height: ALTEZZA_CASELLA,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <span aria-label="non previsto">—</span>
                                                            </Box>
                                                        </TableCell>
                                                    );
                                                }
                                                const checked = grantedCodes.has(voce.code);
                                                const conSelettore = checked && voce.scopeEnforced;
                                                const ambito = checked && !conSelettore ? testoAmbito(voce) : null;
                                                const nota = notaDellaCasella(voce, ambito);
                                                const scopeIniziale = voce.scopeEnforced ? AMBITO_PREDEFINITO : null;
                                                const ambitoScelto = ambitoConcesso.get(voce.code) || AMBITO_PREDEFINITO;
                                                const prezzoDellAmbito = prezzoDi(conSelettore, ambitoScelto, voce);
                                                return (
                                                    <TableCell
                                                        key={azione.code}
                                                        role="cell"
                                                        align="center"
                                                        sx={{ verticalAlign: 'top' }}
                                                    >
                                                        {/* La pillola d'ambito va sotto la casella spuntata (§10.3 mockup screen-4):
                                                            e' solo disposizione, la Box qui non tocca la regola che decide se
                                                            il selettore compare (`conSelettore`, invariata sopra). */}
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <ConNotaAmbito testo={nota} id={`nota-${voce.code}`}>
                                                                <Box sx={{ height: ALTEZZA_CASELLA, display: 'flex', alignItems: 'center' }}>
                                                                    <Checkbox
                                                                        inputProps={{
                                                                            'aria-label': voce.label,
                                                                            'aria-describedby': nota ? `nota-${voce.code}` : undefined,
                                                                        }}
                                                                        checked={checked}
                                                                        disabled={readOnly}
                                                                        onChange={(e) => emettiToggle(voce.code, e.target.checked, scopeIniziale)}
                                                                    />
                                                                </Box>
                                                            </ConNotaAmbito>
                                                            {conSelettore && (
                                                                <SelettoreAmbito
                                                                    voce={voce}
                                                                    ambitoScelto={ambitoScelto}
                                                                    prezzo={prezzoDellAmbito}
                                                                    readOnly={readOnly}
                                                                    onScegli={(scope) => emettiAmbito(voce.code, scope)}
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                {REGOLA_COMPOSIZIONE}
            </Typography>
        </Box>
    );
}

PermissionGrid.propTypes = {
    catalog: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            resourceCode: PropTypes.string.isRequired,
            resourceLabel: PropTypes.string.isRequired,
            groupCode: PropTypes.string.isRequired,
            groupLabel: PropTypes.string.isRequired,
            actionCode: PropTypes.string.isRequired,
            actionLabel: PropTypes.string.isRequired,
            scopeCapability: PropTypes.oneOf(['OWN_SCOPE', 'INHERITED_FROM_CODEX', 'NOT_FILTERABLE']).isRequired,
            scopeEnforced: PropTypes.bool,
            groupScopeKind: PropTypes.string,
            inheritedFromLabel: PropTypes.string,
            ownScopeAllowed: PropTypes.bool,
            nota: PropTypes.string,
        }),
    ).isRequired,
    granted: PropTypes.arrayOf(
        PropTypes.shape({
            permissionCode: PropTypes.string.isRequired,
            scope: PropTypes.string,
        }),
    ).isRequired,
    readOnly: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    otherRoles: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            granted: PropTypes.array.isRequired,
        }),
    ),
};

PermissionGrid.defaultProps = {
    readOnly: false,
    otherRoles: [],
};
